import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box, Typography, Button, Snackbar, Alert,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Accordion, AccordionSummary,
  AccordionDetails, IconButton,
} from '@mui/material'
import {
  CompareArrows, PictureAsPdf, Add, ExpandMore,
  ArrowUpward, ArrowDownward, Remove,
} from '@mui/icons-material'
import { usePlantContext } from '../hooks/usePlantContext'
import { useApi } from '../hooks/useApi'
import { formatNumber, formatPercent } from '../utils/format'
import { status as statusTokens } from '../theme/tokens'

const MONTHS = ['Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec','Jan','Feb','Mar']

const DEMO_PLANS = [
  {
    id: 'plan-001',
    name: 'Budget 2026 — Base Plan',
    status: 'Approved',
    totalCases: 1728000,
    totalECs: 1296000,
    itemsBelowDOS: 4,
    capacityOverloads: 2,
    monthlyCases: [148000,152000,156000,144000,140000,136000,148000,132000,128000,144000,140000,160000],
    monthlyECs: [111000,114000,117000,108000,105000,102000,111000,99000,96000,108000,105000,120000],
    monthlyHours: [602,617,634,585,569,553,602,537,520,585,569,650],
    lineCapacity: {
      'Line 1': { scheduled: [340,330,370,320,310,300,350,280,260,320,310,340], available: [352,336,352,352,336,352,352,320,336,352,320,352] },
      'Line 2': { scheduled: [310,320,350,340,300,290,310,270,250,300,290,320], available: [336,336,336,352,336,336,336,320,336,336,320,336] },
      'Line 3': { scheduled: [280,290,310,270,260,250,280,240,230,260,250,270], available: [320,336,320,320,320,320,320,288,320,320,288,320] },
      'Line 4': { scheduled: [240,250,280,230,220,210,240,200,190,220,210,230], available: [288,288,288,288,288,288,288,256,288,288,256,288] },
    },
  },
  {
    id: 'plan-002',
    name: 'GMI Soups — Full Volume',
    status: 'Not Approved',
    totalCases: 1824000,
    totalECs: 1368000,
    itemsBelowDOS: 6,
    capacityOverloads: 5,
    monthlyCases: [156000,160000,168000,152000,148000,144000,160000,140000,136000,152000,148000,160000],
    monthlyECs: [117000,120000,126000,114000,111000,108000,120000,105000,102000,114000,111000,120000],
    monthlyHours: [634,650,683,617,602,585,650,569,553,617,602,650],
    lineCapacity: {
      'Line 1': { scheduled: [360,350,390,340,330,320,370,300,280,340,330,360], available: [352,336,352,352,336,352,352,320,336,352,320,352] },
      'Line 2': { scheduled: [340,350,380,360,330,320,340,290,270,330,320,350], available: [336,336,336,352,336,336,336,320,336,336,320,336] },
      'Line 3': { scheduled: [300,310,330,290,280,270,300,260,250,280,270,290], available: [320,336,320,320,320,320,320,288,320,320,288,320] },
      'Line 4': { scheduled: [260,270,300,250,240,230,260,220,210,240,230,250], available: [288,288,288,288,288,288,288,256,288,288,256,288] },
    },
  },
  {
    id: 'plan-003',
    name: 'Reduced Forecast — Q3',
    status: 'Not Approved',
    totalCases: 1644000,
    totalECs: 1233000,
    itemsBelowDOS: 2,
    capacityOverloads: 0,
    monthlyCases: [140000,144000,148000,136000,132000,128000,140000,124000,120000,136000,132000,164000],
    monthlyECs: [105000,108000,111000,102000,99000,96000,105000,93000,90000,102000,99000,123000],
    monthlyHours: [569,585,602,553,537,520,569,504,488,553,537,667],
    lineCapacity: {
      'Line 1': { scheduled: [310,320,340,300,290,280,330,260,240,300,290,320], available: [352,336,352,352,336,352,352,320,336,352,320,352] },
      'Line 2': { scheduled: [290,300,330,310,280,270,290,250,230,280,270,300], available: [336,336,336,352,336,336,336,320,336,336,320,336] },
      'Line 3': { scheduled: [260,270,290,250,240,230,260,220,210,240,230,250], available: [320,336,320,320,320,320,320,288,320,320,288,320] },
      'Line 4': { scheduled: [220,230,260,210,200,190,220,180,170,200,190,210], available: [288,288,288,288,288,288,288,256,288,288,256,288] },
    },
  },
]

function DeltaIndicator({ value, invertColor = false }) {
  if (value === 0) return <Remove sx={{ fontSize: 14, color: 'text.disabled' }} />
  const isPositive = value > 0
  const color = invertColor
    ? (isPositive ? statusTokens.critical.color : statusTokens.ok.color)
    : (isPositive ? statusTokens.ok.color : statusTokens.critical.color)
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color }}>
      {isPositive ? <ArrowUpward sx={{ fontSize: 14 }} /> : <ArrowDownward sx={{ fontSize: 14 }} />}
      <Typography variant="caption" sx={{ fontWeight: 600, color }}>
        {isPositive ? '+' : ''}{typeof value === 'number' && Math.abs(value) >= 1000 ? formatNumber(value) : value}
      </Typography>
    </Box>
  )
}

export default function PlanComparison() {
  const { plant } = usePlantContext()
  const { error, get } = useApi()
  const [plans, setPlans] = useState(DEMO_PLANS)
  const [selectedIds, setSelectedIds] = useState([DEMO_PLANS[0].id, DEMO_PLANS[1].id])
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  useEffect(() => {
    let cancelled = false
    get(`/plans?plant=${plant.plantCode}`)
      .then((data) => {
        if (cancelled || !data?.length) return
        const mapped = data.map((p) => ({
          id: p.id,
          name: p.name,
          status: p.status,
          totalCases: p.totalCases12mo || 0,
          totalECs: p.totalECs12mo || 0,
          itemsBelowDOS: 0,
          capacityOverloads: 0,
          monthlyCases: MONTHS.map(() => 0),
          monthlyECs: MONTHS.map(() => 0),
          monthlyHours: MONTHS.map(() => 0),
          lineCapacity: {},
        }))
        setPlans((prev) => {
          const demoById = Object.fromEntries(prev.map((p) => [p.id, p]))
          return mapped.map((p) => demoById[p.id] || p)
        })
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [plant.plantCode]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddPlan = useCallback(() => {
    const unused = plans.find((p) => !selectedIds.includes(p.id))
    if (unused) {
      setSelectedIds((prev) => [...prev, unused.id])
    }
  }, [plans, selectedIds])

  const handleChangePlan = useCallback((index, newId) => {
    setSelectedIds((prev) => {
      const next = [...prev]
      next[index] = newId
      return next
    })
  }, [])

  const selectedPlans = useMemo(
    () => selectedIds.map((id) => plans.find((p) => p.id === id)).filter(Boolean),
    [selectedIds, plans],
  )

  const basePlan = selectedPlans[0]

  const SUMMARY_METRICS = [
    { key: 'totalCases', label: 'Total Cases (12mo)', format: formatNumber },
    { key: 'totalECs', label: 'Total ECs (12mo)', format: formatNumber },
    { key: 'itemsBelowDOS', label: 'Items Below DOS', format: String, invert: true },
    { key: 'capacityOverloads', label: 'Capacity Overloads', format: String, invert: true },
    { key: 'status', label: 'Status', format: String },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <CompareArrows color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Plan Comparison</Typography>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdf />}
          size="small"
          onClick={() => setSnackbar({ open: true, message: 'PDF download will be available in a future release.' })}
        >
          Download PDF
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Compare plans side-by-side for {plant.name}
      </Typography>

      {error && (
        <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>Using demo data — API unavailable</Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {selectedIds.map((id, idx) => (
          <FormControl key={idx} sx={{ minWidth: 260 }}>
            <InputLabel>Plan {String.fromCharCode(65 + idx)}</InputLabel>
            <Select
              value={id}
              label={`Plan ${String.fromCharCode(65 + idx)}`}
              onChange={(e) => handleChangePlan(idx, e.target.value)}
            >
              {plans.map((p) => (
                <MenuItem key={p.id} value={p.id} disabled={selectedIds.includes(p.id) && p.id !== id}>
                  {p.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ))}
        {selectedIds.length < plans.length && (
          <Button startIcon={<Add />} size="small" onClick={handleAddPlan}>
            Add Plan
          </Button>
        )}
      </Box>

      {selectedPlans.length >= 2 && (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Metric</TableCell>
                  {selectedPlans.map((p, i) => (
                    <TableCell key={p.id} align="right" sx={{ fontWeight: 600 }}>
                      <Chip label={String.fromCharCode(65 + i)} size="small" color={i === 0 ? 'primary' : 'default'} variant={i === 0 ? 'filled' : 'outlined'} sx={{ mr: 0.5, fontSize: '0.65rem' }} />
                      {p.name}
                    </TableCell>
                  ))}
                  {selectedPlans.length > 1 && selectedPlans.slice(1).map((p, i) => (
                    <TableCell key={`delta-${p.id}`} align="center" sx={{ fontWeight: 600 }}>
                      Delta ({String.fromCharCode(66 + i)} − A)
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {SUMMARY_METRICS.map((metric) => (
                  <TableRow key={metric.key} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{metric.label}</TableCell>
                    {selectedPlans.map((p) => (
                      <TableCell key={p.id} align="right">{metric.format(p[metric.key])}</TableCell>
                    ))}
                    {selectedPlans.slice(1).map((p) => {
                      const valA = basePlan[metric.key]
                      const valB = p[metric.key]
                      if (typeof valA !== 'number') return <TableCell key={`d-${p.id}`} align="center">—</TableCell>
                      return (
                        <TableCell key={`d-${p.id}`} align="center">
                          <DeltaIndicator value={valB - valA} invertColor={metric.invert} />
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Accordion defaultExpanded={false} variant="outlined" sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Per-Month Breakdown</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ overflowX: 'auto' }}>
              {['Cases','ECs','Hours'].map((label, mi) => {
                const dataKey = mi === 0 ? 'monthlyCases' : mi === 1 ? 'monthlyECs' : 'monthlyHours'
                return (
                  <Box key={label} sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>{label} per Month</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                            {MONTHS.map((m) => (
                              <TableCell key={m} align="right" sx={{ fontWeight: 700 }}>{m}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedPlans.map((p, i) => (
                            <TableRow key={p.id} hover>
                              <TableCell sx={{ fontWeight: 600 }}>
                                <Chip label={String.fromCharCode(65 + i)} size="small" sx={{ mr: 0.5, fontSize: '0.6rem' }} />
                                {p.name}
                              </TableCell>
                              {(p[dataKey] || []).map((v, j) => (
                                <TableCell key={j} align="right">{formatNumber(v)}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )
              })}
            </AccordionDetails>
          </Accordion>

          <Accordion variant="outlined" sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6">Per-Line Capacity Comparison</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ overflowX: 'auto' }}>
              {['Line 1','Line 2','Line 3','Line 4'].map((line) => (
                <Box key={line} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>{line}</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                          {MONTHS.map((m) => (
                            <TableCell key={m} align="right" sx={{ fontWeight: 700 }}>{m}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPlans.map((p, i) => {
                          const cap = p.lineCapacity?.[line]
                          if (!cap) return null
                          return [
                            <TableRow key={`${p.id}-sched`} hover>
                              <TableCell sx={{ fontWeight: 600 }} rowSpan={2}>
                                <Chip label={String.fromCharCode(65 + i)} size="small" sx={{ mr: 0.5, fontSize: '0.6rem' }} />
                                {p.name}
                              </TableCell>
                              <TableCell>Scheduled</TableCell>
                              {cap.scheduled.map((v, j) => (
                                <TableCell key={j} align="right">{formatNumber(v)}</TableCell>
                              ))}
                            </TableRow>,
                            <TableRow key={`${p.id}-avail`} hover>
                              <TableCell>Available</TableCell>
                              {cap.available.map((v, j) => (
                                <TableCell key={j} align="right" sx={{ color: 'text.secondary' }}>{formatNumber(v)}</TableCell>
                              ))}
                            </TableRow>,
                          ]
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  )
}
